import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ChatPreviewCard } from './ChatPreviewCard';
import { PipelinePreviewCard } from './PipelinePreviewCard';
import { TeamStreamPreviewCard } from './TeamStreamPreviewCard';

interface LeadOverviewProps {
  prospect: any;
  prospectCompanies: any[];
  chats: any[];
  pipelines: any[];
  projectProspects: any[];
  kpis: { unseenChats: number; pipelinesCount: number; projectsCount: number };
  onOpenChat: (chatUuid: string) => void;
  onOpenStream: (streamUuid: string) => void;
}

export const LeadOverview: React.FC<LeadOverviewProps> = ({
  prospect,
  prospectCompanies,
  chats,
  pipelines,
  projectProspects,
  kpis,
  onOpenChat,
  onOpenStream,
}) => {
  return (
    <>
      {/* Contact Info Card */}
      {(prospect.email || prospect.phone || prospect.location) && (
        <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Contact Information</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {prospect.email && (
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{prospect.email}</Typography>
              </Box>
            )}
            {prospect.phone && (
              <Box>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{prospect.phone}</Typography>
              </Box>
            )}
            {prospect.location && (
              <Box>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{prospect.location}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Summary & Description */}
      {(prospect.summary || prospect.title_description) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>About</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {prospect.title_description && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Title Description</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{prospect.title_description}</Typography>
              </Box>
            )}
            {prospect.summary && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Summary</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{prospect.summary}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Skills */}
      {prospect.skills && prospect.skills.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Skills</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {prospect.skills.map((skill: string, idx: number) => (
              <Chip key={idx} label={skill} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}

      {/* Education */}
      {prospect.educations && Array.isArray(prospect.educations) && prospect.educations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Education</Typography>
          <Stack spacing={1}>
            {prospect.educations.map((edu: any, idx: number) => (
              <Accordion key={idx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {edu.school || edu.institution || 'Unknown School'}
                    </Typography>
                    {edu.degree && (
                      <Typography variant="caption" color="text.secondary">
                        {edu.degree}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                {(edu.field_of_study || edu.description || edu.date_range) && (
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {edu.field_of_study && (
                        <Typography variant="body2"><strong>Field:</strong> {edu.field_of_study}</Typography>
                      )}
                      {edu.description && (
                        <Typography variant="body2"><strong>Description:</strong> {edu.description}</Typography>
                      )}
                      {edu.date_range && (
                        <Typography variant="body2"><strong>Date:</strong> {edu.date_range}</Typography>
                      )}
                    </Stack>
                  </AccordionDetails>
                )}
              </Accordion>
            ))}
          </Stack>
        </Box>
      )}

      {/* All Companies Experience */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Experience</Typography>
        <Stack spacing={2}>
          {prospectCompanies.map((pc: any, idx: number) => (
            <Box 
              key={pc.key || idx}
              sx={{ 
                p: 2, 
                border: 1, 
                borderColor: pc.still_in_company ? 'primary.main' : 'divider', 
                borderRadius: 1, 
                bgcolor: pc.still_in_company ? 'primary.50' : 'background.paper',
              }}
            >
              {pc.still_in_company && (
                <Chip size="small" label="Current" color="primary" sx={{ mb: 1 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {pc.company?.name || 'Unknown Company'}
              </Typography>
              {pc.role && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {pc.role}
                </Typography>
              )}
              {pc.role_description && (
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {pc.role_description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {pc.duration_in_role && <Chip size="small" label={pc.duration_in_role} />}
                {pc.duration_in_company && <Chip size="small" label={pc.duration_in_company} />}
                {pc.skills && pc.skills.length > 0 && (
                  pc.skills.slice(0, 5).map((skill: string, i: number) => (
                    <Chip key={i} size="small" label={skill} variant="outlined" />
                  ))
                )}
              </Box>
              {(pc.started_at || pc.end_at) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {pc.started_at && new Date(pc.started_at).toLocaleDateString()}
                  {pc.started_at && pc.end_at && ' - '}
                  {pc.end_at && new Date(pc.end_at).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          ))}
          {prospectCompanies.length === 0 && (
            <Typography variant="body2" color="text.secondary">No experience data available</Typography>
          )}
        </Stack>
      </Box>

      {/* KPI Row */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Chip color={kpis.unseenChats ? 'warning' : 'default'} label={`Unseen chats: ${kpis.unseenChats}`} />
        <Chip label={`Active pipelines: ${kpis.pipelinesCount}`} />
        <Chip label={`Experiments: ${kpis.projectsCount}`} />
      </Stack>

      {/* Chats snapshot */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Recent Chats</Typography>
        <Stack spacing={1}>
          {chats.map((c: any) => (
            <ChatPreviewCard key={c.key || c.uuid} chat={c} onOpenChat={onOpenChat} onOpenStream={onOpenStream} />
          ))}
          {chats.length === 0 && (
            <Typography variant="body2" color="text.secondary">No chats yet</Typography>
          )}
        </Stack>
      </Box>

      {/* Pipelines snapshot */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Pipelines</Typography>
        <Stack spacing={1}>
          {pipelines.map((pp: any) => (
            <PipelinePreviewCard key={pp.key || pp.uuid} pipeline={pp} onOpenStream={onOpenStream} />
          ))}
          {pipelines.length === 0 && (
            <Typography variant="body2" color="text.secondary">Not in any pipeline</Typography>
          )}
        </Stack>
      </Box>

      {/* Experiments snapshot */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Experiments</Typography>
        <Stack spacing={1}>
          {projectProspects.map((pp: any) => (
            <TeamStreamPreviewCard key={pp.key || pp.uuid} teamProspect={pp} onOpenStream={onOpenStream} />
          ))}
          {projectProspects.length === 0 && (
            <Typography variant="body2" color="text.secondary">No experiment membership</Typography>
          )}
        </Stack>
      </Box>
    </>
  );
};


